import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * Drag-and-drop photo reordering, tuned for the editor's photo grids.
 *
 * Works with every input the agents actually use: mouse (drag after 6px of
 * movement, so plain clicks on the buttons inside a card still work),
 * touch (long-press ~250ms to lift, so the page still scrolls normally),
 * and keyboard (tab to a card, space to lift, arrows to move, space to drop).
 *
 * `items` may contain duplicate values — ids are de-duplicated internally and
 * reorders are reported by INDEX, so callers can move parallel arrays
 * (photos + captions) together.
 *
 * @param {{
 *   items: string[],
 *   disabled?: boolean,
 *   onReorder: (fromIndex: number, toIndex: number) => void,
 *   container: any,
 *   itemComponent: any,
 *   renderItem: (item: string, index: number) => any,
 * }} props
 */
const SortablePhotoGrid = ({
  items,
  disabled = false,
  onReorder,
  container: Container,
  itemComponent: ItemComponent,
  renderItem,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  /** @type {Record<string, number>} */
  const seen = {};
  const ids = items.map((value) => {
    seen[value] = (seen[value] || 0) + 1;
    return seen[value] === 1 ? value : `${value}~${seen[value]}`;
  });

  /** @param {import("@dnd-kit/core").DragEndEvent} event */
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    if (typeof active.id !== "string" || typeof over.id !== "string") return;
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from !== -1 && to !== -1) onReorder(from, to);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy} disabled={disabled}>
        <Container>
          {items.map((item, index) => (
            <SortableCard
              key={ids[index]}
              id={ids[index]}
              disabled={disabled}
              itemComponent={ItemComponent}
            >
              {renderItem(item, index)}
            </SortableCard>
          ))}
        </Container>
      </SortableContext>
    </DndContext>
  );
};

/** @param {{id: string, disabled: boolean, itemComponent: React.ElementType, children: React.ReactNode}} props */
const SortableCard = ({ id, disabled, itemComponent: ItemComponent, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  /** @type {React.CSSProperties} */
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: disabled ? undefined : "grab",
    touchAction: "manipulation",
    ...(isDragging ? { opacity: 0.6, zIndex: 5, position: "relative" } : {}),
  };
  // dnd-kit's KeyboardSensor starts a drag on Space/Enter and preventDefaults.
  // These listeners sit on the wrapper that also contains each tile's caption
  // <input>, so a space typed into a caption bubbled up, began a keyboard drag,
  // and never reached the field — captions could not contain spaces at all.
  // Keyboard drags still work from the tile itself; only events originating in
  // a control are left alone.
  const dragListeners = { ...(listeners || {}) };
  if (listeners?.onKeyDown) {
    dragListeners.onKeyDown = (/** @type {React.KeyboardEvent} */ event) => {
      const target = /** @type {HTMLElement | null} */ (event.target);
      if (
        target &&
        typeof target.closest === "function" &&
        target.closest("input, textarea, select, button, a, [contenteditable='true']")
      ) {
        return;
      }
      listeners.onKeyDown(event);
    };
  }

  return (
    <ItemComponent
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...dragListeners}
      aria-roledescription="sortable photo"
    >
      {children}
    </ItemComponent>
  );
};

export default SortablePhotoGrid;
